import {MenuDivider, MenuItem} from '@szhsin/react-menu'
import classNames from 'classnames'
import {useCallback, useEffect, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useNavigate} from 'react-router-dom'
import {ImgButton, SimpleBlueButton} from '../../components/core/button'
import ButtonLoader from '../../components/core/button/ButtonLoader'
import CustomSelect from '../../components/core/customSelect'
import Dropdown from '../../components/core/dropdown'
import Tooltip from '../../components/core/tooltip'
import IntervalSettings from '../../components/pages/rulesetIntervalForm'
import intervalUnits from '../../data/intervalUnits.json'
import plans from '../../data/plans.json'
import usageLimitsData from '../../data/usageLimits.json'
import {
    EditIcon,
    FilterIcon,
    HorizontalMoreIcon,
    InstructionIcon,
    PausedIcon,
    PlayIcon,
    PlusIcon,
    TrashIcon
} from '../../icons'
import {routes} from '../../routes'
import {setConfirmIsActive} from '../../stores/alert'
import {getAllLimitUsages} from '../../stores/auth'
import {deleteServerResources, getServerResources} from '../../stores/resources'
import {getServerResource, updateServerResource} from '../../stores/server-monitoring'
import {colors} from '../../utils/colors'
import limitUsageManager from '../../utils/hooks/limitUsageManager'
import renderSkeletonLoaders from '../../utils/loader'
import NavigationHelper from '../../utils/navigation'
import data from './data.json'
import text from './text.json'
import TextHelper from '../../utils/text'
import {getIntervalUnitAndValue} from '../../utils/intervalManagerService'
import urgencyOptions from '../../data/selectOptions/urgency.json'
import Input from '../../components/core/input/index.jsx'
import Textarea from '../../components/core/textarea/index.jsx'
import conditionTypes from '../../data/conditionTypes.json'
import serverMetrics from '../../data/serverMetrics.json'
import Checkbox from '../../components/core/checkbox/index.jsx'

const intervalSelectOptions = Object.values(intervalUnits)?.filter(unitInfo => unitInfo.value !== intervalUnits.second.value)
const DEFAULT_INTERVAL_VALUE = 5

const ActionButton = ({
    onClick,
    isDarkMode,
    isButtonLoader,
    disabled,
    IconComponent,
    buttonText,
    tooltipContent = null,
    className = ''
}) => {
    const button = (
        <ImgButton
            className={classNames('light-bg', className, {
                dark: isDarkMode
            })}
            disabled={disabled}
            onClick={onClick}
        >
            <span>{isButtonLoader && !disabled ? <ButtonLoader /> : <IconComponent />}</span>
            {buttonText && <span className="button-text">{buttonText}</span>}
        </ImgButton>
    )

    return tooltipContent ? (
        <Tooltip placement="top" trigger="mouseenter" content={tooltipContent}>
            {button}
        </Tooltip>
    ) : (
        button
    )
}

const filterMetricsByGroup = (data, groupedMetricTypes = {}, usageLimits, isServerEnabled = true) => {
    const categories = Object.keys(data)
    const result = {}
    categories.map(category => {
        const {usage} = usageLimits[category]
        result[category] = {
            ...data?.[category],
            child_items: data?.[category]?.child_items.filter(item =>
                groupedMetricTypes?.[category]?.includes(item.value)
            ),
            usage: !isServerEnabled ? (groupedMetricTypes?.[category]?.length || 0) + usage : usage
        }
    })
    return result
}

const getRulesetFormInitialValues = (activeModal) => {
    if (!activeModal?.modal_id) return
    const metricOptions = Object.values(serverMetrics)
    const {rulesets} = data.steps
    const selectedMetric = metricOptions.find(metric => metric.value === activeModal?.metric_type)
    const conditionKeyOptions = Object.values(selectedMetric?.charts?.[activeModal?.modal_id]?.condition?.options)

    return {
        ...rulesets.items,
        ...Object.fromEntries(
            Object.entries(rulesets.items).map(([key, item]) => [
                key,
                {
                    ...item,
                    FormItem:
                        item?.form_type === 'select' ? CustomSelect :
                            item?.form_type === 'checkbox' ? Checkbox :
                                item?.form_type === 'textarea' ? Textarea :
                                    Input,
                    ...(item?.form_type === 'select' && {
                        options:
                            item?.name === 'type' ?  [selectedMetric] :
                                item?.name === 'urgency' ? Object.values(urgencyOptions) :
                                    [],
                        value:
                            item?.name === 'type' ? selectedMetric :
                                item?.name === 'urgency' ? urgencyOptions.low :
                                    ''
                    })
                }
            ])
        ),
        conditions: {
            ...rulesets.items.conditions,
            pairs: rulesets.items.conditions.pairs.map(condition => ({
                key: {
                    ...condition.key,
                    FormItem: CustomSelect,
                    options:  conditionKeyOptions,
                    value: conditionKeyOptions[0]
                },
                operator: {
                    ...condition.operator,
                    FormItem: CustomSelect,
                    options: Object.values(conditionTypes.json_key_value_check.values).filter(
                        conditionType => conditionType?.value !== 'contains' && conditionType?.value !== 'not-contains'
                    ),
                    value: conditionTypes.json_key_value_check.values.less_than
                },
                values: {
                    ...condition.values,
                    FormItem: Input
                }
            }))
        }
    }
}

const findMetricByValue = (category, metricValue) => {
    return category?.child_items?.find(item => item.value === metricValue)
}

const getServerResourceMetrics = sr => {
    return !sr ?
        null :
        {
            metric_types_basic: sr?.grouped_metric_types?.basic,
            metric_types_advanced: sr?.grouped_metric_types?.advanced,
            metric_types_complex: sr?.grouped_metric_types?.complex
        }
}

const isStatusDisabled = (selectedMetricTypes, resource, usageLimits) => {
    return (selectedMetricTypes?.basic?.usage > usageLimits?.basic?.value && resource?.grouped_metric_types?.basic?.length > 0)
    || (selectedMetricTypes?.advanced?.usage > usageLimits?.advanced?.value && resource?.grouped_metric_types?.advanced?.length > 0)
    || (selectedMetricTypes?.complex?.usage > usageLimits?.complex?.value && resource?.grouped_metric_types?.complex?.length > 0)
}

const getTooltipText = (resource, selectedMetricTypes, isIntervalLimitOverExceeded, usageLimits) => {
    if (usageLimits?.serverResource?.isLimitExceeded) {
        return TextHelper.replaceAllChars(text.status_disabled_tooltip_text_server, ':limit_title',
            usageLimits.serverResource?.title?.toLowerCase())
    }
    if (isStatusDisabled(selectedMetricTypes, resource, usageLimits)) {
        return TextHelper.replaceAllChars(text.status_disabled_tooltip_text_feature, ':limit_title', 'metric types')
    }
    if (isIntervalLimitOverExceeded && resource?.settings?.interval) {
        return TextHelper.replaceAllChars(text.status_disabled_tooltip_text_feature, ':limit_title',  'incident ruleset')
    }
    return null
}

const useHeaderActions = ({isFetching = false, selectedResources, serverResource, showStatusBtns = true}) => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const dropdownMenuRef = useRef()
    const [state, setState] = useState({
        deletedResourceId: null,
        isButtonLoader: false,
        isButtonDisabled: true,
        intervalValues: {
            interval_value: DEFAULT_INTERVAL_VALUE,
            interval_unit: intervalUnits.minute
        },
        manualUpdate: true,
        activeIncidentsModalId: '',
        isAdvancedEnabled: false
    })
    const [selectedMetricTypes, setSelectedMetricTypes] = useState({})
    const [filteredMetrics, setFilteredMetrics] = useState([])
    const [selectedServerResource, setSelectedServerResource] = useState({})
    const [activeModal, setActiveModal] = useState({type: null, metric_value: null, modal_id: null})
    const rulesetFormInitialValues = getRulesetFormInitialValues(activeModal)
    const serverResources = useSelector(state => state.resources.serverResources)
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const organisationLimitUsages = useSelector((state) => state.auth.organisationLimitUsages)
    const isFreePlan = localStorage.getItem('plan') === plans.free_v2.key
    const selectedResource = serverResources.find(sr => sr?.id === selectedResources?.[0])

    const usageLimits = {
        serverResource: limitUsageManager(usageLimitsData.limit_modules.monitoring.module,
            usageLimitsData.limit_keys.server_resource_count.limit_key, organisationLimitUsages),
        basic: limitUsageManager(usageLimitsData.limit_modules.monitoring.module,
            usageLimitsData.limit_keys.server_basic_resource_count.limit_key, organisationLimitUsages),
        advanced: limitUsageManager(usageLimitsData.limit_modules.monitoring.module,
            usageLimitsData.limit_keys.server_advanced_resource_count.limit_key, organisationLimitUsages),
        complex: limitUsageManager(usageLimitsData.limit_modules.monitoring.module,
            usageLimitsData.limit_keys.server_complex_resource_count.limit_key, organisationLimitUsages),
        incident_ruleset: limitUsageManager(usageLimitsData.limit_modules.incident_management.module,
            usageLimitsData.limit_keys.server_monitoring_incident_ruleset_count.limit_key, organisationLimitUsages)
    }

    const {
        usage: intervalUsage,
        value: intervalLimit
    } = usageLimits.incident_ruleset

    const [interactiveIntervalUsage, setInteractiveIntervalUsage] = useState(intervalUsage)
    const isIntervalLimitExceeded = interactiveIntervalUsage >= intervalLimit
    const isIntervalLimitOverExceeded = interactiveIntervalUsage > intervalLimit

    const handleIncidentMenu = (id) => {
        setState(prev => ({...prev, activeIncidentsModalId: id === state.activeIncidentsModalId ? '' : id}))
    }

    const handleAdvancedSettings = (isManualUpdate = false, isBtnDisabled = false) => {
        const updatedIntervalUsage = !state.isAdvancedEnabled ? interactiveIntervalUsage + 1 : interactiveIntervalUsage - 1
        setState(prev => ({...prev, manualUpdate: isManualUpdate, isAdvancedEnabled: !state.isAdvancedEnabled && !isIntervalLimitExceeded}))
        setInteractiveIntervalUsage(updatedIntervalUsage)
        isBtnDisabled && setState(prev => ({...prev, isButtonDisabled: updatedIntervalUsage > intervalLimit && !state.isAdvancedEnabled}))
    }

    const handleChangeIntervalValues = (name, value, isCheckbox, isUpdateManual = false) => {
        const updatedValues = {...state.intervalValues}
        updatedValues[name] = !isCheckbox ? value.target.value : value
        setState(prev => ({...prev,
            manualUpdate: isUpdateManual,
            isButtonDisabled: isIntervalLimitExceeded,
            intervalValues: updatedValues}))
    }

    const isIntervalSettingDisabled = isFreePlan || (isIntervalLimitExceeded && !state.isAdvancedEnabled)

    const renderIntervalSettings = useCallback((isUpdateManual = false) => (
        <IntervalSettings
            isAdvancedEnabled={state.isAdvancedEnabled}
            onChangeAdvancedSettings={() => handleAdvancedSettings(isUpdateManual, true)}
            onChangeFormValues={(name, value, isCheckbox) => handleChangeIntervalValues(name, value, isCheckbox, isUpdateManual)}
            isIntervalSettingDisabled={isIntervalSettingDisabled}
            values={state.intervalValues}
            intervalLimit={intervalLimit}
            intervalUsage={interactiveIntervalUsage}
            intervalSelectOptions={intervalSelectOptions}
            intervalLabelText={text.interval_based_text}
            intervalInfoText={text.interval_info_text}
            menuPosition="fixed"
            isNewInfoIcon
        />
    ), [handleChangeIntervalValues,
        state.intervalValues,
        intervalLimit,
        intervalUsage,
        state.isAdvancedEnabled,
        handleAdvancedSettings])

    const handleEdit = (updatedResourceId, fromPath) => {
        navigate(NavigationHelper.getNavigationUrl(routes.edit_server_monitoring.path, ':id', updatedResourceId),
            {state: {from: fromPath}})
        dispatch(getAllLimitUsages())
    }

    const handleSelectedMetricTypes = (categoryKey, metricValue, isMetricDisabled = false, isUpdateManual = false) => {
        setState(prev => ({...prev, isButtonDisabled: false, manualUpdate: isUpdateManual}))
        const updatedMetrics = {...selectedMetricTypes}
        const category = updatedMetrics[categoryKey]
        const childItems = category?.child_items || []

        const isMetricSelected = childItems.some((item) => item.value === metricValue)
        const updatedChildItems = isMetricSelected
            ? childItems.filter((item) => item.value !== metricValue)
            : isMetricDisabled
                ? childItems
                : [...childItems, {...findMetricByValue(data.steps.metrics.items[categoryKey], metricValue)}]

        updatedMetrics[categoryKey] = {
            ...category,
            child_items: updatedChildItems,
            usage: isMetricSelected ? category?.usage - 1
                : isMetricDisabled
                    ? category?.usage
                    : category?.usage + 1
        }
        setSelectedMetricTypes(updatedMetrics)
    }

    const openDeleteResourceConfirm = (id) => {
        dispatch(setConfirmIsActive(true))
        if (id?.length === 1) {
            setState(prev => ({...prev, deletedResourceId: id}))
        }
    }

    const deleteResource = async(deletedId) => {
        await dispatch(deleteServerResources(deletedId))
        setState(prev => ({...prev, deletedResourceId: null}))
        navigate(routes.server_monitoring.path)
        await dispatch(getAllLimitUsages())
    }

    const handleStatus = async(serverResource) => {
        setState(prev => ({...prev, isButtonLoader: true}))
        await dispatch(updateServerResource({...serverResource, interval: serverResource?.settings?.interval}, false, serverResource?.id))
        setState(prev => ({...prev, isButtonLoader: false}))
        showStatusBtns ?
            await dispatch(getServerResources()) :
            await dispatch(getServerResource(false, serverResource?.id, navigate, true, true))
        await dispatch(getAllLimitUsages())
        setSelectedServerResource(serverResource)
    }

    const handleAddServer = (navigate, fromPath) => {
        navigate(routes.create_server_monitoring.path, {state: {from: fromPath}})
        dispatch(getAllLimitUsages())
    }

    const getActions = useCallback(({
        state,
        toggleInstructionModal,
        activeMetrics,
        setActiveMetrics,
        serverGroupedMetricValues,
        serverMetricValues,
        onFilter}) => {
        const isSingleResourceSelected = selectedResources?.length === 1
        const isEnableBtnDisabled = isFreePlan || isStatusDisabled(selectedMetricTypes, selectedServerResource, usageLimits) ||
        usageLimits.serverResource.isLimitExceeded || (isIntervalLimitOverExceeded && selectedServerResource?.settings?.interval)

        return (
            <div className="selected-actions">
                {isFetching ? (
                    renderSkeletonLoaders(showStatusBtns ? 4 : 3, 'btn-skeleton')
                ) : (
                    <>
                        {activeMetrics &&
                        <Dropdown
                            align='start'
                            closeMenu={true}
                            innerRef={dropdownMenuRef}
                            menuButton={<button className="filter-button"><FilterIcon />{text.filters}</button>}>
                            <CustomSelect
                                isMulti
                                placeholder={text.select_metrics}
                                options={serverMetricValues}
                                value={activeMetrics}
                                isOptionDisabled={option => !serverGroupedMetricValues?.includes(option?.value)}
                                name="metrics"
                                infoText={text.select_metrics_info}
                                onChange={(_, value) => {
                                    setActiveMetrics(value)
                                }}/>
                            <div className="d-flex w-100 flex-column gap-1">
                                <SimpleBlueButton
                                    className="outline w-100"
                                    onClick={() => onFilter({isReset: true})}>
                                    {text.reset}
                                </SimpleBlueButton>
                                <SimpleBlueButton
                                    className="w-100"
                                    onClick={() => onFilter({isReset: false})}>
                                    {text.apply}
                                </SimpleBlueButton>
                            </div>
                        </Dropdown>}
                        <ActionButton
                            onClick={() => handleAddServer(navigate, state)}
                            isDarkMode={isDarkMode}
                            isButtonLoader={false}
                            disabled={false}
                            IconComponent={() => (
                                <PlusIcon stroke={colors.colorMuted} />
                            )}
                            buttonText=""
                            className="responsive-add-button"
                        />
                        {toggleInstructionModal && <ActionButton
                            onClick={toggleInstructionModal}
                            isDarkMode={isDarkMode}
                            isButtonLoader={false}
                            disabled={false}
                            IconComponent={() => (
                                <InstructionIcon stroke={colors.colorMuted} />
                            )}
                            buttonText={text.instructions}
                        />}
                        {showStatusBtns ? <>
                            <ActionButton
                                onClick={() =>
                                    handleStatus({
                                        ...selectedServerResource,
                                        enabled: false
                                    })
                                }
                                isDarkMode={isDarkMode}
                                isButtonLoader={state.isButtonLoader}
                                disabled={!isSingleResourceSelected || !selectedServerResource?.enabled}
                                IconComponent={PausedIcon}
                                buttonText={text.disable}
                            />
                            <ActionButton
                                onClick={() =>
                                    handleStatus({
                                        ...selectedServerResource,
                                        enabled: true
                                    })
                                }
                                isDarkMode={isDarkMode}
                                isButtonLoader={state.isButtonLoader}
                                disabled={selectedServerResource?.enabled || isEnableBtnDisabled || !isSingleResourceSelected}
                                IconComponent={PlayIcon}
                                buttonText={text.enable}
                                tooltipContent={!selectedServerResource?.enabled && isSingleResourceSelected &&
                                    isEnableBtnDisabled && getTooltipText(selectedServerResource,
                                    selectedMetricTypes, isIntervalLimitOverExceeded, usageLimits)}
                            />
                        </> : selectedServerResource?.enabled ?
                            <ActionButton
                                onClick={() =>
                                    handleStatus({
                                        ...selectedServerResource,
                                        enabled: false
                                    })
                                }
                                isDarkMode={isDarkMode}
                                isButtonLoader={state.isButtonLoader}
                                disabled={!selectedServerResource?.enabled}
                                IconComponent={PausedIcon}
                                buttonText={text.disable}
                            /> :
                            <ActionButton
                                onClick={() =>
                                    handleStatus({
                                        ...selectedServerResource,
                                        enabled: true
                                    })
                                }
                                isDarkMode={isDarkMode}
                                isButtonLoader={state.isButtonLoader}
                                disabled={isEnableBtnDisabled}
                                IconComponent={PlayIcon}
                                buttonText={text.enable}
                                tooltipContent={getTooltipText(selectedServerResource,
                                    selectedMetricTypes, isIntervalLimitOverExceeded, usageLimits)}
                            />}
                        <ActionButton
                            onClick={() =>
                                handleEdit(selectedServerResource?.id, state)
                            }
                            isDarkMode={isDarkMode}
                            isButtonLoader={false}
                            disabled={isFreePlan || (showStatusBtns && !selectedResources?.length)}
                            IconComponent={EditIcon}
                            buttonText={text.edit}
                        />
                        <ActionButton
                            onClick={() =>
                                openDeleteResourceConfirm(selectedResources)
                            }
                            isDarkMode={isDarkMode}
                            isButtonLoader={false}
                            disabled={showStatusBtns && !selectedResources?.length}
                            IconComponent={() => (
                                <TrashIcon stroke={colors.colorRed} />
                            )}
                            buttonText={text.delete}
                            className="outline-red"
                        />
                    </>
                )}
            </div>
        )
    }, [handleAddServer,
        handleEdit,
        selectedServerResource,
        handleStatus,
        openDeleteResourceConfirm,
        isFetching,
        state.isButtonLoader,
        isDarkMode,
        text,
        usageLimits.serverResource])

    const getActionsMenu = (serverResource, state) => {
        const modifiedServerResource = {...serverResource, ...getServerResourceMetrics(serverResource)}
        const isEnableBtnDisabled = !modifiedServerResource?.enabled &&
        (isStatusDisabled(selectedMetricTypes, modifiedServerResource, usageLimits) ||
        usageLimits.serverResource.isLimitExceeded || (isIntervalLimitOverExceeded && modifiedServerResource?.settings?.interval))

        return <Dropdown
            menuButton={<button className={classNames({
                'table-action-btn': true,
                dark: isDarkMode
            })}><HorizontalMoreIcon /></button>}
            onMenuChange={() => setSelectedServerResource(modifiedServerResource)}>
            <MenuItem
                onClick={() => handleEdit(modifiedServerResource?.id, state)}
                disabled={isFreePlan}>
                <span className="d-flex"><EditIcon /></span>
                <span>{text.edit}</span>
            </MenuItem>
            {isEnableBtnDisabled ?
                <Tooltip
                    placement='top'
                    trigger='mouseenter'
                    content={getTooltipText(modifiedServerResource, selectedMetricTypes, isIntervalLimitOverExceeded, usageLimits)} >
                    <MenuItem
                        onClick={() => handleStatus({...modifiedServerResource, enabled: !modifiedServerResource?.enabled})}
                        disabled={true}>
                        <span className="d-flex">{modifiedServerResource?.enabled ? <PausedIcon /> : <PlayIcon />}</span>
                        <span>{text.enable}</span>
                    </MenuItem>
                </Tooltip> :
                <MenuItem
                    onClick={() => handleStatus({...modifiedServerResource, enabled: !modifiedServerResource?.enabled})}
                    disabled={isFreePlan}>
                    <span className="d-flex">{modifiedServerResource?.enabled ? <PausedIcon /> : <PlayIcon />}</span>
                    <span>{modifiedServerResource?.enabled ? text.disable : text.enable}</span>
                </MenuItem>}
            <MenuDivider />
            <MenuItem
                className="danger" onClick={() => openDeleteResourceConfirm([modifiedServerResource?.id])}>
                <span className="d-flex"><TrashIcon stroke={colors.colorRed} /></span>
                <span>{text.delete}</span>
            </MenuItem>
        </Dropdown>
    }

    useEffect(() => {
        dispatch(getAllLimitUsages())
    }, [])

    useEffect(() => {
        if (!state.manualUpdate) {
            return
        }
        setSelectedMetricTypes(
            selectedServerResource?.id ?
                filterMetricsByGroup(data.steps.metrics.items, selectedServerResource?.grouped_metric_types,
                    usageLimits, selectedServerResource?.enabled) :
                {
                    basic: {
                        ...data.steps.metrics.items.basic,
                        usage: usageLimits.basic.usage + 2
                    },
                    advanced: {
                        ...data.steps.metrics.items.advanced,
                        usage: usageLimits.advanced.usage,
                        child_items: []
                    },
                    complex: {
                        ...data.steps.metrics.items.complex,
                        usage: usageLimits.complex.usage,
                        child_items: []
                    }
                }
        )
    }, [usageLimits?.basic?.usage,
        usageLimits?.advanced?.usage,
        usageLimits?.complex?.usage,
        selectedServerResource?.grouped_metric_types?.basic,
        selectedServerResource?.grouped_metric_types?.advanced,
        selectedServerResource?.grouped_metric_types?.complex])

    useEffect(() => {
        setSelectedServerResource(serverResource?.id ?
            ({...serverResource, ...getServerResourceMetrics(serverResource)}) :
            ({...selectedResource, ...getServerResourceMetrics(selectedResource)}))
    }, [serverResource, selectedResource])

    useEffect(() => {
        if (!state.manualUpdate) {
            return
        }
        const updatedIntervalValues = getIntervalUnitAndValue(selectedServerResource?.settings?.interval)
        setState(prev => ({...prev,
            intervalValues: {
                interval_value: selectedServerResource?.settings?.interval ? updatedIntervalValues?.unitValue : DEFAULT_INTERVAL_VALUE,
                interval_unit:  selectedServerResource?.settings?.interval ? intervalUnits?.[updatedIntervalValues?.unit] : intervalUnits.minute
            },
            isAdvancedEnabled: !!selectedServerResource?.settings?.interval}))
        setInteractiveIntervalUsage(
            !!selectedServerResource?.settings?.interval && !selectedServerResource?.enabled ?
                intervalUsage + 1 : intervalUsage)
    }, [selectedServerResource, intervalUsage])

    return {
        handleAddServer,
        getActions,
        getActionsMenu,
        deleteResource,
        state,
        setState,
        activeModal,
        setActiveModal,
        handleSelectedMetricTypes,
        selectedMetricTypes,
        setSelectedMetricTypes,
        filterMetricsByGroup,
        usageLimits,
        setFilteredMetrics,
        filteredMetrics,
        renderIntervalSettings,
        isIntervalLimitOverExceeded,
        setInteractiveIntervalUsage,
        handleIncidentMenu,
        rulesetFormInitialValues
    }
}

export default useHeaderActions
